import { usePageDrawer } from '@app/components/page/drawer/PageDrawerContext';
import { useBreakpointsUp } from '@app/core/hooks/useBreakpointsUp';
import { isVideoStation } from '@app/core/stations/helpers';
import { useAdoverlayEnabled } from '@app/modules/audit/modules/station-audit/hooks/useAdoverlayEnabled';
import { useLowerthirdEnabled } from '@app/modules/audit/hooks/useLowerthirdEnabled';
import { useYoutubeStatsEnabled } from '@app/modules/audit/hooks/useYoutubeStatsEnabled';
import { isDiarizationSegmentsEnabled } from '@app/modules/logger/screens/stream/diarization/isDiarizationSegmentsEnabled';
import { useLiveTopicsEnabledForStation } from '@app/modules/logger/screens/stream/features/live-topics/hooks/useLiveTopicsEnabledForStation';
import { useOpenLiveTopicsDrawer } from '@app/modules/logger/screens/stream/features/live-topics/hooks/useOpenLiveTopicsDrawer';
import { useStreamSettingsStore } from '@app/modules/logger/screens/stream/stores/stream-screen-settings.store';
import { SwitchInput } from '@mi-front/ui';
import AssistantIcon from '@mui/icons-material/Assistant';
import LayersIcon from '@mui/icons-material/Layers';
import { Box, Button, Menu, Tooltip } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useActiveStation } from '../../../../../../../../core/stations/hooks/useActiveStation';
import { isAuditEnabled } from '../../../../../../../audit/helpers';
import { useMentionsEnabledForActiveStation } from '../../../../../../../audit/modules/station-audit/hooks/useMentionsEnabledForActiveStation';

export const StreamFeatureControls = () => {
  const station = useActiveStation();
  const updateSettings = useStreamSettingsStore((state) => state.updateSettings);
  const auditSettings = useStreamSettingsStore((state) => state.audit);
  const { formatMessage, $t } = useIntl();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const liveMentionsEnabled = useMentionsEnabledForActiveStation();
  const diarizationEnabled = isDiarizationSegmentsEnabled(station);
  const isLowerthirdEnabled = useLowerthirdEnabled(station);
  const isVideo = isVideoStation(station);
  const auditEnabled = isAuditEnabled(station);
  const adoverlayEnabled = useAdoverlayEnabled(station);
  const ytstatsEnabled = useYoutubeStatsEnabled(station);
  const liveTopicsEnabled = useLiveTopicsEnabledForStation();
  const openLiveTopicsDrawer = useOpenLiveTopicsDrawer();
  const { open: drawerOpen } = usePageDrawer();
  const { isXl } = useBreakpointsUp();

  const isScreenbugEnabled = isVideo;
  const showButtonLabel = !drawerOpen && isXl;

  const allDisabled =
    !auditEnabled &&
    !liveMentionsEnabled &&
    !diarizationEnabled &&
    !isLowerthirdEnabled &&
    !isScreenbugEnabled &&
    !adoverlayEnabled &&
    !ytstatsEnabled &&
    !liveTopicsEnabled;

  if (allDisabled) {
    return null;
  }

  const anotacionesLabel = formatMessage({
    id: 'audit.controls.markers',
    defaultMessage: 'Anotaciones',
  });

  const liveTopicsLabel = $t({
    id: 'logger.streamScreen.liveTopics.panel.openPanel',
    defaultMessage: 'Live Topics',
  });

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Tooltip title={showButtonLabel ? '' : anotacionesLabel}>
        <Button
          disableElevation
          onClick={handleClick}
          data-testid="stream-feature-controls-options-button"
          startIcon={showButtonLabel ? <LayersIcon /> : undefined}
          variant="outlined"
          color="secondary"
          size="small"
          sx={{
            minWidth: 'auto',
          }}
        >
          {showButtonLabel ? anotacionesLabel : <LayersIcon />}
        </Button>
      </Tooltip>
      <Tooltip title={showButtonLabel ? '' : liveTopicsLabel}>
        <Button
          disableElevation
          onClick={openLiveTopicsDrawer}
          startIcon={showButtonLabel ? <AssistantIcon /> : undefined}
          variant="outlined"
          color="secondary"
          size="small"
          sx={{
            minWidth: 'auto',
          }}
        >
          {showButtonLabel ? liveTopicsLabel : <AssistantIcon />}
        </Button>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          {auditEnabled && (
            <>
              <SwitchInput
                size={'sm'}
                checked={auditSettings.matches}
                onChange={(enabled) =>
                  updateSettings({
                    audit: {
                      matches: enabled,
                    },
                  })
                }
                label={$t({
                  id: 'logger.auditControls.audioMatches',
                  defaultMessage: 'Spot Matches',
                })}
              />
              {liveMentionsEnabled && (
                <SwitchInput
                  size={'sm'}
                  checked={auditSettings.liveMentions}
                  onChange={(enabled) =>
                    updateSettings({
                      audit: {
                        liveMentions: enabled,
                      },
                    })
                  }
                  label={$t({
                    id: 'logger.auditControls.mentionMatches',
                    defaultMessage: 'Live Mentions',
                  })}
                />
              )}
            </>
          )}
          {diarizationEnabled && (
            <SwitchInput
              size={'sm'}
              checked={auditSettings.diarization && !auditSettings.screenbug}
              onChange={(enabled) =>
                updateSettings({
                  audit: {
                    diarization: enabled,
                    screenbug: false,
                  },
                })
              }
              label={formatMessage({
                id: 'audit.transcriptionScreen.speakers',
                defaultMessage: 'Speaker Labels',
              })}
            />
          )}
          {isLowerthirdEnabled && (
            <SwitchInput
              size={'sm'}
              checked={auditSettings.lowerthird}
              onChange={(enabled) =>
                updateSettings({
                  audit: {
                    lowerthird: enabled,
                  },
                })
              }
              label={formatMessage({
                id: 'audit.lowerthird.title',
                defaultMessage: 'Zocalos',
              })}
            />
          )}
          {isScreenbugEnabled && (
            <SwitchInput
              size={'sm'}
              checked={auditSettings.screenbug && !auditSettings.diarization}
              onChange={(enabled) =>
                updateSettings({
                  audit: {
                    screenbug: enabled,
                    diarization: false,
                  },
                })
              }
              label={formatMessage({
                id: 'audit.screenbug.title',
                defaultMessage: 'Mosca',
              })}
            />
          )}
          {adoverlayEnabled && (
            <SwitchInput
              size={'sm'}
              checked={auditSettings.adoverlay}
              onChange={(enabled) =>
                updateSettings({
                  audit: {
                    adoverlay: enabled,
                  },
                })
              }
              label={$t({
                id: 'logger.streamScreen.auditControls.adOverlay',
                defaultMessage: 'Ad overlay',
              })}
            />
          )}
          {ytstatsEnabled && (
            <SwitchInput
              size={'sm'}
              checked={auditSettings.ytstats}
              onChange={(enabled) =>
                updateSettings({
                  audit: {
                    ytstats: enabled,
                  },
                })
              }
              label={$t({
                id: 'logger.streamScreen.auditControls.youTubeViewers',
                defaultMessage: 'YouTube viewers',
              })}
            />
          )}
          {liveTopicsEnabled && (
            <SwitchInput
              size={'sm'}
              checked={auditSettings.liveTopics ?? true}
              onChange={(enabled) =>
                updateSettings({
                  audit: {
                    liveTopics: enabled,
                  },
                })
              }
              label={$t({
                id: 'logger.streamScreen.auditControls.liveTopics',
                defaultMessage: 'Live Topics',
              })}
            />
          )}
        </div>
      </Menu>
    </Box>
  );
};
